import { useState } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, Typography } from "@mui/material"
import { faPen } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import { StandardButton } from "../../../../buttons/StandardButton"
import { SelectedPriceListItem } from "../../../createOrderTypes"
import { ConfirmModal } from "../../../../dialogs/ConfirmModal"
import { OrderContextState } from "../../../mainForm/helpers"

type StyleProps = Readonly<{
    isText: boolean
}>
const useStyles = makeStyles<StyleProps>()((theme, { isText }) => ({
    button: {
        "&:hover": {
            background: theme.palette.common.white,
            cursor: isText ? "default" : "pointer",
        },
    },
}))

type PriceListEditButtonProps = Readonly<{
    selectedPriceList: SelectedPriceListItem
    handleOpenSelectPriceListsDialog: () => void
    orderContext: OrderContextState
    isDisabledEdit: boolean
}>

export function PriceListEditButton({
    selectedPriceList,
    handleOpenSelectPriceListsDialog,
    orderContext,
    isDisabledEdit,
}: PriceListEditButtonProps) {
    const { merchantName, priceListName, isOnlyOnePriceList } = selectedPriceList
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [{ value: deliveries }, , helpers] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const isDisabledEditElements = isDisabledEdit || isOnlyOnePriceList

    const hasProducts = deliveries?.deliveryTimes.some((delivery) => delivery.orderLines.length > 0)

    const handleEditPrice = () => {
        if (hasProducts && orderContext.mode === "create") {
            setShowConfirmModal(true)
        } else handleOpenSelectPriceListsDialog()
    }
    const handleConfirm = () => {
        if (deliveries) {
            const updatedDeliveries: DeliveryTimesField = {
                ...deliveries,
                deliveryTimes: deliveries.deliveryTimes.map((delivery) => ({
                    ...delivery,
                    orderLines: [],
                })),
            }

            helpers.setValue(updatedDeliveries)
        }

        handleOpenSelectPriceListsDialog()
        setShowConfirmModal(false)
    }
    const { classes } = useStyles({ isText: isDisabledEditElements })

    return (
        <Box flex="none">
            <StandardButton
                size="small"
                color="white"
                fullWidth={false}
                onClick={isDisabledEditElements ? undefined : handleEditPrice}
                updateDefaultClasses={classes.button}
                endIcon={isDisabledEditElements ? undefined : <FontAwesomeIcon icon={faPen} />}
                disableRipple
            >
                {merchantName}: {priceListName}
            </StandardButton>
            {showConfirmModal && (
                <LocalizedStrict
                    id="create-order-price-list-confirm-dialog"
                    attrs={{ title: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        open={showConfirmModal}
                        onClose={() => setShowConfirmModal(false)}
                        handleClick={handleConfirm}
                        title="Remove products"
                        buttonLabelSubmit="Remove"
                    >
                        <LocalizedStrict id="create-order-price-list-confirm-dialog-message">
                            <Typography textAlign="center" variant="body1">
                                Changing the period will clear your current selection. Do you want to continue?
                            </Typography>
                        </LocalizedStrict>
                    </ConfirmModal>
                </LocalizedStrict>
            )}
        </Box>
    )
}
